import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { Shield, AlertTriangle, FileText, Scale, CheckCircle } from 'lucide-react';

// Persistent compliance banner for employer dashboard
export const ComplianceBanner = ({ profile, onDismiss }) => {
  if (profile?.compliance_banner_dismissed_at) return null;

  return (
    <div className="bg-gradient-to-r from-[#F0B429]/10 to-[#F0B429]/5 border border-[#F0B429]/20 rounded-xl p-4 mb-6 animate-fade-in">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-[#F0B429]/20 flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-[#F0B429]" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[#0A0F1E] mb-1">HR Compliance Reminder</h3>
          <p className="text-[#5B6472] text-sm leading-relaxed">
            VETT records contain employment verification data. Ensure all information is accurate, 
            non-discriminatory, and complies with EEOC guidelines. Records should focus on job-related 
            performance and documented facts only.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <a 
              href="/terms" 
              target="_blank" 
              className="text-[#1B5AFF] text-sm hover:underline flex items-center gap-1"
            >
              <FileText className="w-3.5 h-3.5" />
              Terms of Service
            </a>
            <a 
              href="/privacy" 
              target="_blank" 
              className="text-[#1B5AFF] text-sm hover:underline flex items-center gap-1"
            >
              <Scale className="w-3.5 h-3.5" />
              Privacy Policy
            </a>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="text-[#5B6472] hover:text-[#0A0F1E]"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};

// Initial compliance acknowledgment modal (shown once on first visit)
export const ComplianceAcknowledgmentModal = ({ open, onAcknowledge }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAcknowledge = async () => {
    if (!acknowledged) {
      toast.error('Please acknowledge the compliance guidelines');
      return;
    }
    setLoading(true);
    try {
      await onAcknowledge();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] max-w-lg" hideCloseButton>
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/10 flex items-center justify-center mb-4">
            <Shield className="w-6 h-6 text-[#1B5AFF]" />
          </div>
          <DialogTitle className="text-xl">HR Compliance Guidelines</DialogTitle>
          <DialogDescription className="text-[#5B6472]">
            Before creating VETT records, please review our compliance guidelines.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 rounded-xl bg-[#F5F8FF] border border-[#E8EDF5] space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#00C9A7] flex-shrink-0 mt-0.5" />
              <p className="text-[#0A0F1E] text-sm">
                All employment records must be factual and based on documented performance data.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#00C9A7] flex-shrink-0 mt-0.5" />
              <p className="text-[#0A0F1E] text-sm">
                Records must not contain information about protected characteristics (race, gender, age, religion, disability, etc.).
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#00C9A7] flex-shrink-0 mt-0.5" />
              <p className="text-[#0A0F1E] text-sm">
                Focus on job-relevant skills, behaviors, and measurable outcomes.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#00C9A7] flex-shrink-0 mt-0.5" />
              <p className="text-[#0A0F1E] text-sm">
                Rehire decisions must be based on job performance, not personal factors.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[#F0B429]/10 border border-[#F0B429]/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F0B429] flex-shrink-0 mt-0.5" />
              <p className="text-[#0A0F1E] text-sm">
                Violating EEOC guidelines or providing false information may result in account suspension 
                and potential legal liability.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="compliance-ack"
              checked={acknowledged}
              onCheckedChange={setAcknowledged}
              data-testid="compliance-checkbox"
            />
            <Label htmlFor="compliance-ack" className="text-[#0A0F1E] text-sm cursor-pointer leading-relaxed">
              I understand and agree to follow EEOC compliance guidelines when creating employee records. 
              I confirm that all information I provide will be accurate, job-related, and non-discriminatory.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAcknowledge}
            disabled={!acknowledged || loading}
            className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl"
            data-testid="acknowledge-compliance-btn"
          >
            {loading ? 'Processing...' : 'I Acknowledge & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hiring compliance modal (shown before viewing talent profiles)
export const HiringComplianceModal = ({ open, onAcknowledge }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAcknowledge = async () => {
    if (!acknowledged) {
      toast.error('Please acknowledge the hiring compliance guidelines');
      return;
    }
    setLoading(true);
    try {
      await onAcknowledge();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] max-w-lg" hideCloseButton>
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/10 flex items-center justify-center mb-4">
            <Scale className="w-6 h-6 text-[#1B5AFF]" />
          </div>
          <DialogTitle className="text-xl">Fair Hiring Practices</DialogTitle>
          <DialogDescription className="text-[#5B6472]">
            Before accessing candidate profiles, please acknowledge these guidelines.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 rounded-xl bg-[#F5F8FF] border border-[#E8EDF5] space-y-3">
            <p className="text-[#0A0F1E] text-sm leading-relaxed">
              When evaluating candidates through VETT, you agree to:
            </p>
            <ul className="space-y-2 text-[#0A0F1E] text-sm">
              <li className="flex items-start gap-2">
                <span className="text-[#1B5AFF]">•</span>
                Make hiring decisions based solely on job-related qualifications and documented performance
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1B5AFF]">•</span>
                Not discriminate based on protected characteristics
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1B5AFF]">•</span>
                Use VETT data as one factor among many in your evaluation process
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1B5AFF]">•</span>
                Keep candidate information confidential
              </li>
            </ul>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="hiring-compliance-ack"
              checked={acknowledged}
              onCheckedChange={setAcknowledged}
              data-testid="hiring-compliance-checkbox"
            />
            <Label htmlFor="hiring-compliance-ack" className="text-[#0A0F1E] text-sm cursor-pointer leading-relaxed">
              I agree to follow fair hiring practices and EEOC guidelines when reviewing candidates.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAcknowledge}
            disabled={!acknowledged || loading}
            className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl"
            data-testid="acknowledge-hiring-btn"
          >
            {loading ? 'Processing...' : 'Continue to Talent Search'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hook to manage compliance state
export const useComplianceState = (profile) => {
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showHiringModal, setShowHiringModal] = useState(false);

  useEffect(() => {
    if (profile && !profile.compliance_acknowledged_at) {
      setShowComplianceModal(true);
    }
  }, [profile]);

  const acknowledgeCompliance = async () => {
    try {
      await employerAPI.updateProfile({ 
        compliance_acknowledged_at: new Date().toISOString() 
      });
      setShowComplianceModal(false);
      toast.success('Compliance guidelines acknowledged');
    } catch (error) {
      toast.error('Failed to save acknowledgment');
      throw error;
    }
  };

  const acknowledgeHiringCompliance = async () => {
    try {
      await employerAPI.updateProfile({ 
        hiring_compliance_acknowledged_at: new Date().toISOString() 
      });
      setShowHiringModal(false);
    } catch (error) {
      toast.error('Failed to save acknowledgment');
      throw error;
    }
  };

  const dismissBanner = async () => {
    try {
      await employerAPI.updateProfile({ 
        compliance_banner_dismissed_at: new Date().toISOString() 
      });
    } catch (error) {
      console.error('Failed to dismiss banner:', error);
    }
  };

  const checkHiringCompliance = () => {
    if (profile && !profile.hiring_compliance_acknowledged_at) {
      setShowHiringModal(true);
      return false;
    }
    return true;
  };

  return {
    showComplianceModal,
    showHiringModal,
    acknowledgeCompliance,
    acknowledgeHiringCompliance,
    dismissBanner,
    checkHiringCompliance
  };
};

export default {
  ComplianceBanner,
  ComplianceAcknowledgmentModal,
  HiringComplianceModal,
  useComplianceState
};
